import { Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { NmdCalculationResponseDto, NmdCalculationResponseWrapperDto, PrintResultDto } from '../../../core/services/GPRMV1Service.Generated';
import { ValuePerPhaseModel } from '../../../shared/models/value-per-phase.model';
import { environment } from '../../../../environments/environment';
import { ENmdProductStatus } from '../../../shared/enums/nmd-product-status.enum';
import { DataSetSettingEnum } from '../../../shared/enums/data-set-setting.enum';
import { AppService } from '../../../core/services/app.service';
import { ModulesEnum } from '../../../shared/enums/modules.enum';
import { TranslateService } from '@ngx-translate/core';
import { getConnectionTypes, getConnectionAccessibility, getPiercing, getEdgeEnclosure } from '../../../shared/utils/dismountability-utils';

@Component({
    selector: 'app-print-mpg-elements-landscape',
    templateUrl: './print-mpg-elements-landscape.component.html',
    styleUrls: ['./print-mpg-elements-landscape.component.scss'],
    standalone: false
})
export class PrintMpgElementsLandscapeComponent implements OnChanges {
    @Input() elementResultTitle: string;
    @Input() calculationResults: NmdCalculationResponseDto | null = null;
    @Input() calculationResultsAfter: NmdCalculationResponseDto | null = null;
    @Input() mpgResultTitle: string;
    @Input() isExistingBuildingCal: boolean;
    @Input() selectedDataSet: number;
    @Input() mpgResultTitlePerMainElementTitle: string;
    @Input() printData: PrintResultDto;
    @Input() envirnmentalCategoryTitle: string;
    @Input() environmentEffects: ValuePerPhaseModel[];
    @Input() environmentEffectsAfter: ValuePerPhaseModel[];


    uploadsImageBaseUrl: string = environment.uploadsBaseUrl;
    nmdProductStatusEnum = ENmdProductStatus;
    dataSetSetting = DataSetSettingEnum;
    isHnnModuleAccessible: boolean = false;
    isHNNIndicatorEnable: boolean = false;
    modulesEnum = ModulesEnum;
    connectionTypes: any[] = [];
    connectionAccessibility: any[] = [];
    piercing: any[] = [];
    edgeEnclosure: any[] = [];

    // Debug counts (useful to show why template renders no items)
    totalDrawers: number | undefined;
    totalAddedElements: number | undefined;
    totalNmdElements: number | undefined;
    totalProducts: number | undefined;
    totalActiveProducts: number | undefined;

    // Cache the first visible product key to avoid repeated tree scans from the template
    private firstVisibleProductKey: string | null = null;

    mkiTitle: string;
    ppiTitle: string;
    constructor(private _appService: AppService, private _translate: TranslateService, private _cd: ChangeDetectorRef) {}

    ngOnInit() {
        this.isHnnModuleAccessible = this._appService.isModuleAccessible(this.modulesEnum.Hnn);
        // populate connection lists using shared utilities
        this.bindDropdownValues();
        this.setHeaderTitles();
    }

    setHeaderTitles() {
        if (this.selectedDataSet === DataSetSettingEnum.A1) {
            this.mkiTitle = this._translate.instant('PRINT_PAGE.MKI_A1');
            this.ppiTitle = this._translate.instant('PRINT_PAGE.PPI_A1');
        }
        else if (this.selectedDataSet === DataSetSettingEnum.A2) {
            this.mkiTitle = this._translate.instant('PRINT_PAGE.MKI_A2');
            this.ppiTitle = this._translate.instant('PRINT_PAGE.PPI_A2');
        }
        else {
            this.mkiTitle = this._translate.instant('PRINT_PAGE.MKI_A1ANDA2');
            this.ppiTitle = this._translate.instant('PRINT_PAGE.PPI_A1ANDA2');
        }
    }

    ngOnChanges(changes: SimpleChanges) {

        // When inputs change, ensure dependent UI values are updated and counts recomputed.
        // If the selected dataset changed, update header titles.
        if (changes['selectedDataSet']) {
            this.setHeaderTitles();
        }

        // Decide which calculation response to check depending on selectedDataSet:
        // - A1A2 uses `calculationResultsAfter` (A2/new building results)
        // - otherwise use `calculationResults` (A1 or current)
        const responseToCheck = this.selectedDataSet === DataSetSettingEnum.A1A2 ? this.calculationResultsAfter : this.calculationResults;

        // If either the selected dataset or the relevant calculation input changed,
        // re-evaluate the HNN indicator.
        const relevantCalcChange = (this.selectedDataSet === DataSetSettingEnum.A1A2 && changes['calculationResultsAfter'])
            || (this.selectedDataSet !== DataSetSettingEnum.A1A2 && changes['calculationResults']);

        if (changes['selectedDataSet'] || relevantCalcChange) {
            this.isHNNIndicatorEnable = !!responseToCheck?.hnnResults;
        }

        if (changes['printData'] && this.printData) {
            // Compute counts and determine the first visible active product in
            // a single traversal to avoid O(n) duplication on large trees.
            this.computeCounts();
            try { this._cd.detectChanges(); } catch (e) { /* ignore */ }
        }
    }

    private computeCounts() {
        try {
            const pd = this.printData;
            const totalDrawers = pd?.drawers?.length || 0;
            let totalAddedElements = 0;
            let totalNmdElements = 0;
            let totalProducts = 0;
            let totalActiveProducts = 0;
            // Reset cached first-visible product key before traversal
            this.firstVisibleProductKey = null;

            const drawers = pd?.drawers || [];
            for (let dIndex = 0; dIndex < drawers.length; dIndex++) {
                const d = drawers[dIndex];
                const aels = d?.addedElements || [];
                totalAddedElements += aels.length;
                for (let aIndex = 0; aIndex < aels.length; aIndex++) {
                    const ae = aels[aIndex];
                    const nmds = ae?.nmdElements || [];
                    totalNmdElements += nmds.length;
                    for (let nIndex = 0; nIndex < nmds.length; nIndex++) {
                        const nmd = nmds[nIndex];
                        const prods = nmd?.products || [];
                        totalProducts += prods.length;
                        for (let pIndex = 0; pIndex < prods.length; pIndex++) {
                            const p = prods[pIndex];
                            if (p?.productStatusId === this.nmdProductStatusEnum.Active) {
                                totalActiveProducts++;
                                // Cache the first visible active product key if not set
                                if (!this.firstVisibleProductKey && !(ae?.noActiveProductsInThisElement)) {
                                    this.firstVisibleProductKey = `${dIndex}:${aIndex}:${nIndex}:${pIndex}`;
                                }
                            }
                        }
                    }
                }
            }
            this.totalDrawers = totalDrawers;
            this.totalAddedElements = totalAddedElements;
            this.totalNmdElements = totalNmdElements;
            this.totalProducts = totalProducts;
            this.totalActiveProducts = totalActiveProducts;
        } catch (e) {
            this.totalDrawers = this.totalAddedElements = this.totalNmdElements = this.totalProducts = this.totalActiveProducts = 0;
        }
    }

    /**
   * return boolean value if A1 data exists in the item
   */
    hasA1Data(item: any): boolean {
        return item.precalculations?.some(p => p.assessmentStrategyId === DataSetSettingEnum.A1);
    }

    /**
    * return boolean value if A2 data exists in the item
    */
    hasA2Data(item: any): boolean {
        return item.precalculations?.some(p => p.assessmentStrategyId === DataSetSettingEnum.A2);
    }


    bindDropdownValues() {
        this.connectionTypes = getConnectionTypes();
        this.connectionAccessibility = getConnectionAccessibility();
        this.piercing = getPiercing();
        this.edgeEnclosure = getEdgeEnclosure();
    }

    /**
     * Return connection type name for a product.
     * It prefers product.dismountability?.connectionTypeId and falls back to
     * product.epicProductData.connectionTypeId. If id not found or not mapped,
     * returns empty string.
     */
    getConnectionTypeName(product: any): string {
        const id = product?.dismountability?.connectionTypeId ?? product?.epicProductData?.connectionTypeId;
        if (!id) { return ''; }
        const found = this.connectionTypes.find(ct => ct.id === id);
        return found ? found.label : '';
    }

    getConnectionAccessibility(product: any): string {
        const id = product?.dismountability?.connectionAccessibilityId ?? product?.epicProductData?.connectionAccessibilityId;
        if (!id) { return ''; }
        const found = this.connectionAccessibility.find(ct => ct.id === id);
        return found ? found.label : '';
    }

    getEdgeEnclosure(product: any): string {
        const id = product?.dismountability?.edgeEnclosureId ?? product?.epicProductData?.edgeEnclosureId;
        if (!id) { return ''; }
        const found = this.edgeEnclosure.find(ct => ct.id === id);
        return found ? found.label : '';
    }

    getPiercing(product: any): string {
        const id = product?.dismountability?.piercingId ?? product?.epicProductData?.piercingId;
        if (!id) { return ''; }
        const found = this.piercing.find(ct => ct.id === id);
        return found ? found.label : '';
    }

    calculateTotalMass(product: any) {
        var massPerUnit = product?.originAndReuse?.mass ?? product?.epicProductData?.mass;
        var amountPerUnit = product.amountPerUnit;

        if (typeof massPerUnit === 'string') {
            massPerUnit = parseFloat(massPerUnit.replace(',', '.'));
        }
        if (typeof amountPerUnit === 'string') {
            amountPerUnit = parseFloat(amountPerUnit.replace(',', '.'));
        }

        if (isNaN(massPerUnit)) massPerUnit = 0;
        if (isNaN(amountPerUnit)) amountPerUnit = 0;

        return (massPerUnit * amountPerUnit).toFixed(2).replace('.', ',');
    }

    calculateTotalVolume(product: any) {
        var volumePerUnit = product?.originAndReuse?.volume;
        var amountPerUnit = product.amountPerUnit;
        if (typeof volumePerUnit === 'string') {
            volumePerUnit = parseFloat(volumePerUnit.replace(',', '.'));
        }
        if (typeof amountPerUnit === 'string') {
            amountPerUnit = parseFloat(amountPerUnit.replace(',', '.'));
        }

        if (isNaN(volumePerUnit)) volumePerUnit = 0;
        if (isNaN(amountPerUnit)) amountPerUnit = 0;

        return (volumePerUnit * amountPerUnit).toFixed(2).replace('.', ',');
    }

    /**
     * Determine whether a drawer should be visible in the print output.
     * A drawer is visible when it has at least one addedElement that is not
     * marked as `noActiveProductsInThisElement`.
     */
    hasVisibleAddedElements(drawer: any): boolean {
        if (!drawer) return false;
        const added = drawer?.addedElements || [];
        if (!added || added.length === 0) return false;
        return added.some(ae => !ae?.noActiveProductsInThisElement);
    }

    /**
     * Return true when the current product (identified by drawer/indexes) is the
     * first visible active product in the whole printData. This is used to show
     * column header labels (Scaling, Quantity, MKI/PPI) next to the first rendered
     * product even if it appears in a later drawer.
     */
    isFirstVisibleProduct(di: number, ai: number, ni: number, pi: number): boolean {
        if (!this.firstVisibleProductKey) return false;
        const key = `${di}:${ai}:${ni}:${pi}`;
        return key === this.firstVisibleProductKey;
    }
}